import { parseDateTimeLocal, formatarDataLocal, getTodaySaoPaulo } from "@/lib/dateUtils";
import type { Venda } from "@/lib/api";

/** Metas fixas de faturamento mensal (valores em R$). */
export const metasFaturamento = [2000, 5000, 10000, 20000, 50000, 100000] as const;

/** Usar cores do cliente via CSS vars quando possível. */
export const CLIENT_META_ORANGE = "var(--client-primary, #3B82F6)";
export const CLIENT_META_TRACK = "var(--client-secondary, #3B82F633)";

/** Formata valor da meta na tabela/modal: R$ 2K, R$ 100K */
export function formatMetaValorReais(meta: number): string {
  if (meta >= 1000 && meta % 1000 === 0) {
    return `R$ ${meta / 1000}K`;
  }
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(meta);
}

/** Formata parte curta tipo 1.3K, 5.2K, 12K, 100K+ */
export function formatFaturamentoCurtoK(total: number): string {
  if (total >= 100000) return "100K+";
  const k = total / 1000;
  if (k <= 0) return "0K";
  const rounded1 = Math.round(k * 10) / 10;
  if (Number.isInteger(rounded1) || Math.abs(rounded1 - Math.round(rounded1)) < 0.001) {
    return `${Math.round(rounded1)}K`;
  }
  return `${rounded1}K`;
}

/** Label da meta alvo na barra: 2K, 10K, 100K */
export function formatMetaAlvoCurto(meta: number): string {
  if (meta >= 1000) return `${meta / 1000}K`;
  return String(meta);
}

export type MetaConquistaRow = {
  meta: number;
  metaLabel: string;
  dataConquista: string | null;
  conquistado: boolean;
};

/**
 * Vendas do mês em ordem cronológica (mais antiga primeiro).
 */
export function ordenarVendasMesAsc(vendas: Venda[]): Venda[] {
  return [...vendas].sort((a, b) => {
    const ta = parseDateTimeLocal(a.createdAt).getTime();
    const tb = parseDateTimeLocal(b.createdAt).getTime();
    return ta - tb;
  });
}

/**
 * Para cada meta, data em que o acumulado mensal atingiu a meta (data da venda que cruzou).
 */
export function calcularDatasConquistaMetas(vendasAsc: Venda[], metas: readonly number[]): Map<number, Date | null> {
  const result = new Map<number, Date | null>();
  for (const m of metas) result.set(m, null);

  let acumulado = 0;
  const metasPendentes = new Set(metas);

  for (const v of vendasAsc) {
    acumulado += Number(v.total ?? 0);
    const d = parseDateTimeLocal(v.createdAt);
    for (const meta of metas) {
      if (metasPendentes.has(meta) && acumulado >= meta) {
        result.set(meta, d);
        metasPendentes.delete(meta);
      }
    }
    if (metasPendentes.size === 0) break;
  }

  return result;
}

export function montarLinhasModal(
  vendasDoMes: Venda[],
  metas: readonly number[] = metasFaturamento,
): MetaConquistaRow[] {
  const asc = ordenarVendasMesAsc(vendasDoMes);
  const datas = calcularDatasConquistaMetas(asc, metas);

  return metas.map((meta) => {
    const dt = datas.get(meta) ?? null;
    return {
      meta,
      metaLabel: formatMetaValorReais(meta),
      dataConquista: dt ? formatarDataLocal(dt) : null,
      conquistado: dt != null,
    };
  });
}

export type BarraMetaResult = {
  textoLinha: string;
  progressoPct: number;
  proximaMeta: number | null;
  faturamentoMes: number;
};

/**
 * Próxima meta não atingida; se todas atingidas e < 100k ainda mostra próxima slice;
 * se >= 100k barra cheia e texto 100K+ / 100K.
 */
export function calcularBarraMetaMensal(
  faturamentoMes: number,
  metas: readonly number[] = metasFaturamento,
): BarraMetaResult {
  const ultimaMeta = metas[metas.length - 1];

  if (faturamentoMes >= ultimaMeta) {
    return {
      textoLinha: `R$ ${formatFaturamentoCurtoK(faturamentoMes)} / ${formatMetaAlvoCurto(ultimaMeta)}`,
      progressoPct: 100,
      proximaMeta: ultimaMeta,
      faturamentoMes,
    };
  }

  const proxima = metas.find((m) => faturamentoMes < m) ?? ultimaMeta;
  const pct = proxima > 0 ? Math.min(100, (faturamentoMes / proxima) * 100) : 0;

  return {
    textoLinha: `R$ ${formatFaturamentoCurtoK(faturamentoMes)} / ${formatMetaAlvoCurto(proxima)}`,
    progressoPct: pct,
    proximaMeta: proxima,
    faturamentoMes,
  };
}

export function totalFaturamentoVendas(vendas: Venda[]): number {
  return vendas.reduce((s, v) => s + Number(v.total ?? 0), 0);
}

/** Primeiro e último dia do mês corrente (America/Sao_Paulo), formato YYYY-MM-DD. */
export function getMesAtualRangeIsoSP(): { start: string; end: string } {
  const today = getTodaySaoPaulo();
  const [ys, ms] = today.split("-");
  const y = Number(ys);
  const m = Number(ms);
  const first = `${ys}-${ms}-01`;
  const lastDay = new Date(y, m, 0).getDate();
  const end = `${ys}-${ms}-${String(lastDay).padStart(2, "0")}`;
  return { start: first, end };
}
